import React, { useEffect, useRef } from "react";
import { useJsApiLoader } from "@react-google-maps/api";
import { GOOGLE_MAPS_JS_LOADER_OPTIONS } from "../../../config/googleMapsJsLoaderOptions";
import { resolvePlaceDisplayAddress } from "./placeAddress";
import "./GoogleAddressAutocompleteInput.css";

const PLACES_COUNTRY_NG = { country: "ng" };
const PAC_CLASS = "google-address-pac";
const DEFAULT_PLACE_TYPES = ["address"];
const PLACE_FIELDS = [
  "place_id",
  "formatted_address",
  "address_components",
  "name",
  "vicinity",
];

const removeExtraPacContainers = () => {
  const pacs = document.querySelectorAll(".pac-container");
  if (pacs.length <= 1) return;
  for (let i = 0; i < pacs.length - 1; i += 1) {
    pacs[i].remove();
  }
};

const GoogleAddressAutocompleteInput = ({
  value,
  onChange,
  placeholder = "Search for a street address",
  icon = null,
  wrapperClassName = "",
  embedded = false,
  anchorRef: externalAnchorRef = null,
  placeTypes = DEFAULT_PLACE_TYPES,
  inputClassName = "w-full min-w-0 outline-none text-sm text-[#000929] placeholder-[#9CA3AF] bg-transparent",
  inputName,
  onKeyDown,
  showLoadError = true,
}) => {
  const { isLoaded, loadError } = useJsApiLoader(GOOGLE_MAPS_JS_LOADER_OPTIONS);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const onChangeRef = useRef(onChange);
  const syncRafRef = useRef(null);
  const placeTypesKey = placeTypes?.length
    ? placeTypes.join("|")
    : "none";

  const getAnchorEl = () =>
    externalAnchorRef?.current ?? wrapperRef.current;

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  const applySelectedPlace = (place, inputEl) => {
    const g = window.google;
    const inputText = inputEl?.value?.trim() || "";
    let address = resolvePlaceDisplayAddress(place, inputText);

    if (address) {
      onChangeRef.current(address);
      return;
    }

    if (!place?.place_id || !g?.maps?.places) return;

    const service = new g.maps.places.PlacesService(
      document.createElement("div")
    );
    service.getDetails(
      {
        placeId: place.place_id,
        fields: PLACE_FIELDS,
      },
      (result, status) => {
        if (status !== g.maps.places.PlacesServiceStatus.OK || !result) return;
        const resolved = resolvePlaceDisplayAddress(result, inputText);
        if (resolved) onChangeRef.current(resolved);
      }
    );
  };

  const syncPacDropdown = () => {
    removeExtraPacContainers();

    const wrap = getAnchorEl();
    const pac = document.querySelector(".pac-container");
    if (!wrap || !pac) return;

    pac.classList.add(PAC_CLASS);

    const pacStyle = window.getComputedStyle(pac);
    if (pacStyle.display === "none" || pacStyle.visibility === "hidden") return;

    const rect = wrap.getBoundingClientRect();
    pac.style.setProperty("position", "fixed", "important");
    pac.style.setProperty("left", `${rect.left}px`, "important");
    pac.style.setProperty("top", `${rect.bottom + 4}px`, "important");
    pac.style.setProperty("width", `${rect.width}px`, "important");
    pac.style.setProperty("min-width", `${rect.width}px`, "important");
    pac.style.setProperty("max-width", `${rect.width}px`, "important");
  };

  const schedulePacSync = () => {
    if (syncRafRef.current) cancelAnimationFrame(syncRafRef.current);
    syncRafRef.current = requestAnimationFrame(syncPacDropdown);
  };

  useEffect(() => {
    if (!isLoaded || loadError) return;

    const input = inputRef.current;
    if (!input) return;

    const onFocus = () => schedulePacSync();
    const onInput = () => schedulePacSync();
    const onWindowChange = () => schedulePacSync();

    input.addEventListener("focus", onFocus);
    input.addEventListener("input", onInput);
    window.addEventListener("resize", onWindowChange);
    window.addEventListener("scroll", onWindowChange, true);

    return () => {
      input.removeEventListener("focus", onFocus);
      input.removeEventListener("input", onInput);
      window.removeEventListener("resize", onWindowChange);
      window.removeEventListener("scroll", onWindowChange, true);
      if (syncRafRef.current) cancelAnimationFrame(syncRafRef.current);
    };
  }, [isLoaded, loadError]);

  useEffect(() => {
    if (!isLoaded || loadError) return;
    const g = window.google;
    if (!g?.maps?.places) return;
    const input = inputRef.current;
    if (!input) return;

    if (autocompleteRef.current) {
      g.maps.event.clearInstanceListeners(autocompleteRef.current);
      autocompleteRef.current = null;
    }

    const acOptions = {
      componentRestrictions: PLACES_COUNTRY_NG,
      fields: PLACE_FIELDS,
    };
    if (placeTypes && placeTypes.length > 0) {
      acOptions.types = placeTypes;
    }

    const ac = new g.maps.places.Autocomplete(input, acOptions);
    autocompleteRef.current = ac;

    ac.addListener("place_changed", () => {
      window.setTimeout(() => {
        const place = ac.getPlace();
        applySelectedPlace(place, input);
        schedulePacSync();
      }, 0);
    });

    removeExtraPacContainers();

    return () => {
      if (g?.maps?.event && ac) {
        g.maps.event.clearInstanceListeners(ac);
      }
      autocompleteRef.current = null;
      document.querySelectorAll(".pac-container").forEach((el) => el.remove());
    };
  }, [isLoaded, loadError, placeTypesKey]);

  const inputEl = (
    <input
      ref={inputRef}
      type="text"
      name={inputName}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={onKeyDown}
      placeholder={
        loadError
          ? "Address search unavailable"
          : isLoaded
            ? placeholder
            : "Loading address search…"
      }
      autoComplete="off"
      disabled={!isLoaded && !loadError}
      className={inputClassName}
    />
  );

  if (embedded) {
    return (
      <div className="relative flex-1 min-w-0">
        <div
          ref={wrapperRef}
          className={`flex items-center gap-2 flex-1 min-w-0 ${wrapperClassName}`}
        >
          {icon}
          {inputEl}
        </div>
        {showLoadError && loadError ? (
          <p className="absolute left-0 top-full mt-1 text-xs text-red-600 z-10">
            Location search unavailable.
          </p>
        ) : null}
      </div>
    );
  }

  return (
    <div className="relative w-full">
      <div
        ref={wrapperRef}
        className={`flex items-center gap-2 border border-[#E5E7EB] rounded-xl px-4 py-3 bg-white ${wrapperClassName}`}
      >
        {icon}
        {inputEl}
      </div>
      {showLoadError && loadError ? (
        <p className="mt-1.5 text-xs text-red-600">
          Address search could not load. Check your Google API key and Places
          API.
        </p>
      ) : null}
    </div>
  );
};

export default GoogleAddressAutocompleteInput;
